/**
 * Frontend audit-label helpers.
 *
 * The authoritative value -> label map comes from the /event-types payload
 * (backend audit_labels registry). These helpers are a fallback for unknown /
 * legacy values and for category + severity presentation - NO duplicated label
 * table lives here.
 */

import type { AuditEventTypeOption, AuditLog } from '../../../services/auditApi'

const CATEGORY_PREFIXES = new Set([
  'auth',
  'sync',
  'user',
  'access',
  'admin',
  'data',
  'maintenance',
  'feedback',
])

/**
 * Display fallback mirroring the backend `humanise`: split on '.' or '_',
 * drop a leading category segment, Title-case the first word.
 */
export function humaniseEventType(value: string): string {
  if (!value) return value
  let parts = value.split(/[._]/).filter(Boolean)
  if (value.includes('.') && parts.length > 1 && CATEGORY_PREFIXES.has(parts[0])) {
    parts = parts.slice(1)
  }
  const text = parts.join(' ')
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : value
}

/** Build a value -> label map from the /event-types payload. */
export function buildLabelMap(options?: AuditEventTypeOption[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const opt of options || []) map.set(opt.value, opt.label)
  return map
}

/** Look up a humanised label, preferring the registry payload. */
export function labelFor(value: string, labelMap: Map<string, string>): string {
  return labelMap.get(value) || humaniseEventType(value)
}

/** Build a value -> category map from the /event-types payload. */
export function buildCategoryMap(options?: AuditEventTypeOption[]): Map<string, string> {
  const map = new Map<string, string>()
  for (const opt of options || []) map.set(opt.value, opt.category)
  return map
}

/** "SYNC_OPERATIONS" -> "Sync operations" for the muted category sub-chip. */
export function formatCategory(category?: string): string {
  if (!category) return ''
  const text = category.replace(/_/g, ' ')
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

/** Short chip label for the Event column category sub-chip. */
const CATEGORY_SHORT_LABELS: Record<string, string> = {
  AUTHENTICATION: 'Auth',
  ACCESS_CONTROL: 'Access',
  ADMINISTRATION: 'Admin',
  SYNC_OPERATIONS: 'Sync',
  SYSTEM_MAINTENANCE: 'System',
  DATA_ACCESS: 'Data',
  USER_MANAGEMENT: 'Users',
  USER_FEEDBACK: 'Feedback',
}

export function shortCategory(category?: string): string {
  if (!category) return ''
  return CATEGORY_SHORT_LABELS[category] || formatCategory(category)
}

const COUNTRY_CODES: Record<string, string> = { au: 'AU', nz: 'NZ', us: 'US' }

/** Country code (AU/NZ/US) derived from the fp_code prefix, or '' if unknown. */
export function countryForFp(fpCode?: string): string {
  if (!fpCode || fpCode.length < 2) return ''
  return COUNTRY_CODES[fpCode.slice(0, 2).toLowerCase()] || ''
}

/**
 * Scope of an audit row: the affected FP (+ country) or Global/System.
 * `code` is shown as the primary chip, `country` as a small market badge.
 */
export function scopeFor(log: AuditLog): { code: string; country: string } {
  if (log.fp_code) {
    return { code: log.fp_code.toUpperCase(), country: countryForFp(log.fp_code) }
  }
  if (log.event_category === 'SYSTEM_MAINTENANCE') return { code: 'System', country: '' }
  return { code: 'Global', country: '' }
}

/**
 * Target of an audit row: resource type + reference, falling back to the
 * request endpoint, else '-'. Avoids duplicating the Event summary.
 */
export function targetFor(log: AuditLog): { primary: string; secondary: string } {
  const ref = log.resource_name || log.resource_id || ''
  if (log.resource_type) return { primary: log.resource_type, secondary: ref }
  if (ref) return { primary: ref, secondary: '' }
  if (log.request_path) return { primary: log.request_path, secondary: '' }
  return { primary: '-', secondary: '' }
}

const RESULT_LABELS: Record<string, string> = {
  true: 'Success',
  false: 'Failed',
  partial: 'Partial',
}

export function resultLabel(success: string): string {
  return RESULT_LABELS[success] || success
}

/**
 * Whether a row should get the subtle failed/security tint: any non-success
 * outcome, or an error/critical severity (security denials log as error).
 */
export function rowFlagged(log: Pick<AuditLog, 'success' | 'severity'>): boolean {
  return log.success !== 'true' || log.severity === 'error' || log.severity === 'critical'
}

const ACCENTED = new Set(['warning', 'error', 'critical'])

/** MUI palette colour for a severity, or undefined when it should stay neutral. */
export function severityColor(
  severity?: string
): 'warning' | 'error' | undefined {
  if (!severity || !ACCENTED.has(severity)) return undefined
  return severity === 'warning' ? 'warning' : 'error'
}

export const SEVERITY_LABELS: Record<string, string> = {
  info: 'Info',
  notice: 'Notice',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical',
}
